import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import TemplatePreview from "./TemplatePreview";
import SuggestionEditor, { FieldError, inputClass } from "./SuggestionEditor";
import { createTemplate, getTemplate, isAgentNotConfigured } from "./rcsTemplateApi";
import {
  TEMPLATE_TYPES,
  buildTemplateData,
  dataPathToFormKey,
  emptyCard,
  emptyForm,
  formFromData,
  validateTemplateForm,
} from "./templatePayload";

const MAX_CAROUSEL_CARDS = 10;

const TYPE_DESCRIPTIONS = {
  basic: "Plain text message with optional buttons.",
  standalone: "A single card with media, title, description and buttons.",
  carousel: "2 or more swipeable cards.",
};

// Immutably sets a dotted path such as "carousel.cards.1.title".
const setIn = (obj, path, value) => {
  const [head, ...rest] = path;
  const copy = Array.isArray(obj) ? [...obj] : { ...obj };
  copy[head] = rest.length ? setIn(obj[head], rest, value) : value;
  return copy;
};

const Field = ({ label, error, hint, children }) => (
  <label className="block">
    <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
    {children}
    {hint && !error && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    <FieldError error={error} />
  </label>
);

const CardForm = ({ card, prefix, errors, onField, onSuggestions, showHeight = true }) => {
  const err = (f) => errors[`${prefix}.${f}`];
  return (
    <div className="space-y-4">
      <Field label={`Title (${card.title.length}/200)`} error={err("title")}>
        <input
          value={card.title}
          onChange={(e) => onField(`${prefix}.title`, e.target.value)}
          className={inputClass(err("title"))}
          placeholder="e.g. Hi {{1}}, your offer is here"
        />
      </Field>
      <Field label={`Description (${card.description.length}/2000)`} error={err("description")}>
        <textarea
          rows={3}
          value={card.description}
          onChange={(e) => onField(`${prefix}.description`, e.target.value)}
          className={inputClass(err("description"))}
        />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Media URL (optional)" error={err("mediaUrl")}>
          <input
            value={card.mediaUrl}
            onChange={(e) => onField(`${prefix}.mediaUrl`, e.target.value)}
            className={inputClass(err("mediaUrl"))}
            placeholder="https://… or {{1}}"
          />
        </Field>
        <Field label="Thumbnail URL (optional)" error={err("thumbnailUrl")}>
          <input
            value={card.thumbnailUrl}
            onChange={(e) => onField(`${prefix}.thumbnailUrl`, e.target.value)}
            className={inputClass(err("thumbnailUrl"))}
            placeholder="https://… or {{1}}"
          />
        </Field>
      </div>
      {showHeight && card.mediaUrl && (
        <Field label="Media height">
          <select
            value={card.mediaHeight}
            onChange={(e) => onField(`${prefix}.mediaHeight`, e.target.value)}
            className={inputClass()}
          >
            <option value="SHORT">Short</option>
            <option value="MEDIUM">Medium</option>
            <option value="TALL">Tall</option>
          </select>
        </Field>
      )}
      <SuggestionEditor
        suggestions={card.suggestions}
        onChange={(list) => onSuggestions(`${prefix}.suggestions`, list)}
        errors={errors}
        prefix={prefix}
      />
    </div>
  );
};

const CreateTemplate = ({ nav, onAgentMissing, fromId }) => {
  const [form, setForm] = useState(null);
  const [loadingSource, setLoadingSource] = useState(!!fromId);
  const [touched, setTouched] = useState(() => new Set());
  const [showAll, setShowAll] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [serverSummary, setServerSummary] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // "Create new version": prefill from an existing template.
  useEffect(() => {
    if (!fromId) return;
    let cancelled = false;
    (async () => {
      const res = await getTemplate(fromId);
      if (cancelled) return;
      setLoadingSource(false);
      if (res.ok) {
        const t = res.data.data;
        setForm(formFromData(t.data, `${t.template_name} v2`));
      } else if (isAgentNotConfigured(res)) {
        onAgentMissing();
      } else {
        toast.error(res.status === 404 ? "Template not found" : res.error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fromId, onAgentMissing]);

  const clientErrors = useMemo(() => (form ? validateTemplateForm(form) : {}), [form]);
  const data = useMemo(() => (form ? buildTemplateData(form) : null), [form]);

  const isVisible = (key) =>
    showAll ||
    [...touched].some((t) => key === t || (t.endsWith(".suggestions") && key.startsWith(t)));

  const errors = {};
  Object.entries(clientErrors).forEach(([k, v]) => {
    if (isVisible(k)) errors[k] = v;
  });
  Object.assign(errors, serverErrors);

  const clientErrorCount = Object.keys(clientErrors).length;

  const touch = (key) =>
    setTouched((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));

  const clearServerError = (key) =>
    setServerErrors((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([k]) => k !== key && !k.startsWith(`${key}.`))
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });

  const onField = (key, value) => {
    setForm((prev) => setIn(prev, key.split("."), value));
    touch(key);
    clearServerError(key);
  };

  const pickType = (type) => {
    setForm(emptyForm(type));
    setTouched(new Set());
    setShowAll(false);
    setServerErrors({});
    setServerSummary([]);
  };

  const submit = async () => {
    if (clientErrorCount) {
      setShowAll(true);
      return;
    }
    setSubmitting(true);
    setServerErrors({});
    setServerSummary([]);
    const res = await createTemplate({ template_name: form.template_name.trim(), data });
    setSubmitting(false);

    if (res.ok) {
      toast.success("Template submitted for approval");
      nav.detail(res.data.data.id);
      return;
    }
    if (isAgentNotConfigured(res)) {
      onAgentMissing();
      return;
    }
    if (res.status === 422 && res.errors && !Array.isArray(res.errors)) {
      const fieldErrs = {};
      const summary = [];
      if (res.errors.template_name?.length) {
        fieldErrs.template_name = res.errors.template_name.join(", ");
      }
      (res.errors.data || []).forEach((entry) => {
        const idx = entry.indexOf(": ");
        const path = idx === -1 ? "" : entry.slice(0, idx);
        const message = idx === -1 ? entry : entry.slice(idx + 2);
        const key = path && dataPathToFormKey(path, form.type);
        if (key && !fieldErrs[key]) fieldErrs[key] = message;
        else summary.push(entry);
      });
      Object.entries(res.errors).forEach(([k, v]) => {
        if (k !== "template_name" && k !== "data") summary.push(`${k}: ${[].concat(v).join(", ")}`);
      });
      setServerErrors(fieldErrs);
      setServerSummary(summary);
      return;
    }
    if (res.status === 422 && res.data?.source === "jiocx") {
      Swal.fire({ title: "Template Rejected", text: res.error, icon: "error" });
      return;
    }
    if (res.status === 502) {
      const result = await Swal.fire({
        title: "Service is unavailable",
        text: res.error,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Retry",
        cancelButtonText: "Cancel",
      });
      if (result.isConfirmed) submit();
      return;
    }
    Swal.fire({ title: "Couldn't create the template", text: res.error, icon: "error" });
  };

  const header = (
    <div className="flex items-center gap-3 mb-6">
      <button
        onClick={() => nav.list()}
        className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
        title="Back to templates"
      >
        <ArrowLeft size={18} />
      </button>
      <h1 className="text-2xl font-bold text-gray-800">Create RCS template</h1>
    </div>
  );

  if (loadingSource) {
    return (
      <div>
        {header}
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#00b6ce]" />
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div>
        {header}
        <p className="text-sm text-gray-500 mb-4">Choose a template type</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
          {TEMPLATE_TYPES.map((t) => (
            <button
              key={t.value}
              onClick={() => pickType(t.value)}
              className="text-left bg-white rounded-xl border border-gray-200 p-5 hover:border-[#00b6ce] hover:shadow-md transition"
            >
              <div className="font-semibold text-gray-800 mb-1">{t.label}</div>
              <div className="text-xs text-gray-500">{TYPE_DESCRIPTIONS[t.value]}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const cards = form.carousel.cards;

  return (
    <div>
      {header}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500">Type:</span>
            {TEMPLATE_TYPES.map((t) => (
              <button
                key={t.value}
                onClick={() => form.type !== t.value && setForm({ ...form, type: t.value })}
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                  form.type === t.value
                    ? "bg-[#00b6ce] text-white border-[#00b6ce]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-[#00b6ce]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <Field label="Template name" error={errors.template_name}>
            <input
              value={form.template_name}
              onChange={(e) => onField("template_name", e.target.value)}
              className={inputClass(errors.template_name)}
              maxLength={120}
              placeholder="e.g. Diwali Offer"
            />
          </Field>

          {form.type === "basic" && (
            <>
              <Field label={`Message text`} error={errors["basic.text"]}>
                <textarea
                  rows={5}
                  value={form.basic.text}
                  onChange={(e) => onField("basic.text", e.target.value)}
                  className={inputClass(errors["basic.text"])}
                  placeholder="Hi {{1}}, your order {{2}} has shipped."
                />
              </Field>
              <SuggestionEditor
                suggestions={form.basic.suggestions}
                onChange={(list) => onField("basic.suggestions", list)}
                errors={errors}
                prefix="basic"
              />
            </>
          )}

          {form.type === "standalone" && (
            <>
              <Field label="Card orientation">
                <select
                  value={form.standalone.orientation}
                  onChange={(e) => onField("standalone.orientation", e.target.value)}
                  className={inputClass()}
                >
                  <option value="VERTICAL">Vertical</option>
                  <option value="HORIZONTAL">Horizontal</option>
                </select>
              </Field>
              <CardForm
                card={form.standalone.card}
                prefix="standalone.card"
                errors={errors}
                onField={onField}
                onSuggestions={onField}
                showHeight={form.standalone.orientation === "VERTICAL"}
              />
            </>
          )}

          {form.type === "carousel" && (
            <>
              <Field label="Card width">
                <select
                  value={form.carousel.cardWidth}
                  onChange={(e) => onField("carousel.cardWidth", e.target.value)}
                  className={inputClass()}
                >
                  <option value="SMALL_WIDTH">Small</option>
                  <option value="MEDIUM_WIDTH">Medium</option>
                </select>
              </Field>
              <FieldError error={errors["carousel.cards"]} />
              {cards.map((card, i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-gray-700 text-sm">Card {i + 1}</span>
                    <button
                      type="button"
                      disabled={cards.length <= 2}
                      onClick={() =>
                        onField(
                          "carousel.cards",
                          cards.filter((_, j) => j !== i)
                        )
                      }
                      className="text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:hover:text-gray-400 p-1"
                      title={cards.length <= 2 ? "A carousel needs at least 2 cards" : "Remove card"}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <CardForm
                    card={card}
                    prefix={`carousel.cards.${i}`}
                    errors={errors}
                    onField={onField}
                    onSuggestions={onField}
                  />
                </div>
              ))}
              {cards.length < MAX_CAROUSEL_CARDS && (
                <button
                  type="button"
                  onClick={() => onField("carousel.cards", [...cards, emptyCard()])}
                  className="flex items-center gap-1 text-sm font-semibold text-[#00b6ce] hover:text-[#009baf]"
                >
                  <Plus size={16} /> Add card
                </button>
              )}
            </>
          )}

          <p className="text-xs text-gray-400 border-t border-gray-100 pt-3">
            Use {"{{1}}"}, {"{{2}}"}… for variables. Numbering restarts in every field: a title{" "}
            {"{{1}}"} and a description {"{{1}}"} are separate variables.
          </p>

          {serverSummary.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <div className="font-semibold mb-1">The server rejected the template:</div>
              <ul className="list-disc pl-5 space-y-0.5">
                {serverSummary.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              onClick={submit}
              disabled={submitting || clientErrorCount > 0}
              className="bg-[#00b6ce] hover:bg-[#009baf] disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2 rounded-lg text-sm font-semibold"
            >
              {submitting ? "Submitting…" : "Submit for approval"}
            </button>
            <button
              onClick={() => nav.list()}
              disabled={submitting}
              className="px-5 py-2 rounded-lg text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50"
            >
              Cancel
            </button>
            {clientErrorCount > 0 && (
              <span className="text-xs text-red-600">
                {clientErrorCount} {clientErrorCount === 1 ? "issue" : "issues"} to fix
                {!showAll && (
                  <>
                    {" · "}
                    <button onClick={() => setShowAll(true)} className="underline">
                      show all
                    </button>
                  </>
                )}
              </span>
            )}
          </div>
        </div>

        <div className="lg:sticky lg:top-4">
          <div className="text-sm font-semibold text-gray-600 mb-2 text-center">Preview</div>
          <TemplatePreview data={data} />
        </div>
      </div>
    </div>
  );
};

export default CreateTemplate;
